import { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { partitionDisplayStatus } from './partitionStatus'

/* ================================================================
   Types
   ================================================================ */

interface Assignment {
    id: number
    resource_id: number | null
    partition_id: number | null
    opportunity_id: number | null
    opportunity_name: string | null
    /** Redmine project linked to that opportunity (identifier / id / name), when any. */
    redmine_identifier?: string | null
    redmine_project_id?: number | null
    redmine_project_name?: string | null
    start_date: string | null
    end_date: string | null
    notes: string | null
    assigned_by: string | null
    assigned_at: string | null
}

interface Partition {
    id: number
    gpu_id: number
    partition_name: string
    mig_profile: string
    memory_gb: number | null
    status: string
    assignment: Assignment | null
}

interface Resource {
    id: number
    name: string
    environment: 'on_prem' | 'aws'
    resource_type: string
    node_name: string | null
    specs: string | null
    ip_address: string | null
    endpoint: string | null
    status: string
    notes: string | null
    partitions: Partition[]
    assignments: Assignment[]
}

interface Opportunity {
    id: number
    name: string
}

/* ================================================================
   Styled Components
   ================================================================ */

const Page = styled.div`
    padding: ${({ theme }) => theme.spacing.lg};
`

const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const PageTitle = styled.h1`
    font-size: ${({ theme }) => theme.fontSizes.xl};
    font-weight: 700;
    margin: 0;
`

const TabBar = styled.div`
    display: flex;
    gap: 2px;
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    overflow: hidden;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const Tab = styled.button<{ $active: boolean }>`
    padding: 0.7rem 1.5rem;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all ${({ theme }) => theme.transitions.normal};
    background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
    color: ${({ $active, theme }) => ($active ? 'white' : theme.colors.text)};

    &:hover {
        background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'rgba(0,62,87,0.1)')};
    }
`

const SummaryCards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const SummaryCard = styled.div`
    background: ${({ theme }) => theme.colors.cardBg};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing.lg};
    box-shadow: ${({ theme }) => theme.shadows.sm};
    text-align: center;
`

const SummaryValue = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.xxl};
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
`

const SummaryLabel = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.xs};
    color: ${({ theme }) => theme.colors.textMuted};
    margin-top: 4px;
`

const Card = styled.div`
    background: ${({ theme }) => theme.colors.cardBg};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.sm};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    overflow: hidden;
`

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.secondary};
`

const CardTitle = styled.h3`
    margin: 0;
    font-size: ${({ theme }) => theme.fontSizes.md};
    font-weight: 600;
`

const CardBody = styled.div`
    padding: ${({ theme }) => theme.spacing.lg};
`

const Badge = styled.span<{ $color?: string }>`
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: ${({ theme }) => theme.fontSizes.xs};
    font-weight: 600;
    background: ${({ $color }) => $color || '#e0e0e0'}22;
    color: ${({ $color }) => $color || '#666'};
    border: 1px solid ${({ $color }) => $color || '#ddd'};
`

const statusColor = (s: string) => {
    switch (s) {
        case 'active': case 'available': return '#4CAF50'
        case 'assigned': return '#2196F3'
        case 'maintenance': return '#FF9800'
        case 'retired': return '#9E9E9E'
        default: return '#666'
    }
}

const statusLabel = (s: string) => {
    switch (s) {
        case 'active': return 'Activo'
        case 'available': return 'Disponible'
        case 'assigned': return 'Asignada'
        case 'maintenance': return 'Mantenimiento'
        case 'retired': return 'Retirado'
        default: return s
    }
}

const envLabel = (e: string) => e === 'on_prem' ? '🖥️ On-Prem' : '☁️ AWS'

const Btn = styled.button<{ $variant?: string }>`
    padding: 6px 14px;
    border: 1px solid ${({ theme, $variant }) =>
        $variant === 'danger' ? theme.colors.danger :
            $variant === 'primary' ? theme.colors.primary : theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: ${({ theme }) => theme.fontSizes.xs};
    font-weight: 600;
    cursor: pointer;
    transition: all ${({ theme }) => theme.transitions.fast};
    background: ${({ $variant, theme }) =>
        $variant === 'danger' ? theme.colors.danger :
            $variant === 'primary' ? theme.colors.primary : 'white'};
    color: ${({ $variant }) => ($variant === 'danger' || $variant === 'primary') ? 'white' : '#333'};

    &:hover { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const BtnGroup = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: ${({ theme }) => theme.fontSizes.sm};

    th, td {
        text-align: left;
        padding: 10px 12px;
        border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }
    th {
        font-weight: 600;
        color: ${({ theme }) => theme.colors.textMuted};
        font-size: ${({ theme }) => theme.fontSizes.xs};
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    tr:last-child td { border-bottom: none; }
    tr:hover td { background: ${({ theme }) => theme.colors.secondary}44; }
`

const PartitionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: ${({ theme }) => theme.spacing.md};
    margin-top: ${({ theme }) => theme.spacing.md};
`

const PartitionCard = styled.div<{ $status: string }>`
    border: 2px solid ${({ $status }) => statusColor($status)};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing.md};
    background: ${({ $status }) => statusColor($status)}08;
`

const PartitionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`

const PartitionName = styled.span`
    font-weight: 600;
    font-size: ${({ theme }) => theme.fontSizes.sm};
`

const InfoRow = styled.div`
    display: flex;
    gap: 4px;
    align-items: center;
    font-size: ${({ theme }) => theme.fontSizes.xs};
    color: ${({ theme }) => theme.colors.textMuted};
    margin-bottom: 4px;
`

const FormOverlay = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`

const FormModal = styled.div`
    background: white;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    padding: ${({ theme }) => theme.spacing.xl};
    width: 520px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: ${({ theme }) => theme.shadows.xl};
`

const FormTitle = styled.h3`
    margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
    font-size: ${({ theme }) => theme.fontSizes.lg};
`

const FormGroup = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing.md};
`

const FormLabel = styled.label`
    display: block;
    font-size: ${({ theme }) => theme.fontSizes.xs};
    font-weight: 600;
    margin-bottom: 4px;
    color: ${({ theme }) => theme.colors.textMuted};
`

const FormInput = styled.input`
    width: 100%;
    padding: 8px 10px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.primary};
    }
`

const FormSelect = styled.select`
    width: 100%;
    padding: 8px 10px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    cursor: pointer;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.primary};
    }
`

const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${({ theme }) => theme.spacing.md};
`

const Alert = styled.div<{ $type: 'success' | 'error' }>`
    padding: 10px 16px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    background: ${({ $type }) => $type === 'success' ? '#E8F5E9' : '#FFEBEE'};
    color: ${({ $type }) => $type === 'success' ? '#2E7D32' : '#C62828'};
    border: 1px solid ${({ $type }) => $type === 'success' ? '#A5D6A7' : '#EF9A9A'};
`

const EmptyState = styled.div`
    text-align: center;
    padding: ${({ theme }) => theme.spacing.xxl};
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: ${({ theme }) => theme.fontSizes.sm};
`

/* ================================================================
   Main Component
   ================================================================ */

export const InfrastructurePage: React.FC = () => {
    const { user } = useAuth()
    const isAdmin = user?.role?.toLowerCase() === 'admin'
    // Same rule as the nav tab (Header) and the backend check: the route itself
    // is open to everyone, so a user without the flag must get a clear message
    // here rather than a failed request and an empty page.
    const canViewInfra = isAdmin || !!user?.can_view_infra

    const [resources, setResources] = useState<Resource[]>([])
    const [opportunities, setOpportunities] = useState<Opportunity[]>([])
    const [activeTab, setActiveTab] = useState<'on_prem' | 'aws'>('on_prem')
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
    const [loading, setLoading] = useState(true)
    const [accessDenied, setAccessDenied] = useState(false)

    // Modal state
    const [showResourceModal, setShowResourceModal] = useState(false)
    const [editingResource, setEditingResource] = useState<Resource | null>(null)
    const [showPartitionModal, setShowPartitionModal] = useState(false)
    const [partitionGpuId, setPartitionGpuId] = useState<number | null>(null)
    const [editingPartition, setEditingPartition] = useState<Partition | null>(null)
    const [showAssignModal, setShowAssignModal] = useState(false)
    const [assignTarget, setAssignTarget] = useState<{ resourceId?: number; partitionId?: number } | null>(null)
    const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null)
    const [refreshing, setRefreshing] = useState(false)

    const handleRefresh = useCallback(async () => {
        setRefreshing(true)
        try {
            const res = await api.infrastructure.refresh()
            const data = res.data
            const errors = data.errors?.length ? `\n${data.errors.join('\n')}` : ''
            const hasGpus = data.gpus?.length > 0
            setAlert({
                type: data.errors?.length && !hasGpus ? 'error' : 'success',
                message: hasGpus
                    ? `Infraestructura actualizada: ${data.gpus.length} servidor(es) detectado(s), ${data.partitions_updated || 0} particiones actualizadas.${errors}`
                    : `No se pudieron detectar servidores GPU.${errors}`,
            })
            loadResources()
        } catch {
            setAlert({ type: 'error', message: 'Error al refrescar la infraestructura.' })
        } finally {
            setRefreshing(false)
        }
    }, [])

    const loadResources = useCallback(async () => {
        try {
            const res = await api.infrastructure.getAll()
            setResources(res.data)
            setAccessDenied(false)
        } catch (err: any) {
            // 403 = access revoked while the page was open (the backend reads
            // the flag live); not a load failure worth a red banner.
            if (err?.response?.status === 403) setAccessDenied(true)
            else setAlert({ type: 'error', message: 'Error al cargar los recursos.' })
        } finally {
            setLoading(false)
        }
    }, [])

    const loadOpportunities = useCallback(async () => {
        try {
            const res = await api.opportunities.getAll()
            setOpportunities(res.data.map((o: any) => ({ id: o.id, name: o.general?.name || o.name || `#${o.id}` })))
        } catch { /* ignore */ }
    }, [])

    useEffect(() => {
        if (!canViewInfra) {
            setLoading(false)
            return
        }
        loadResources()
        loadOpportunities()
    }, [canViewInfra, loadResources, loadOpportunities])

    useEffect(() => {
        if (alert) {
            const t = setTimeout(() => setAlert(null), 4000)
            return () => clearTimeout(t)
        }
    }, [alert])

    const filtered = resources.filter(r => r.environment === activeTab)
    const onPremCount = resources.filter(r => r.environment === 'on_prem').length
    const awsCount = resources.filter(r => r.environment === 'aws').length
    const totalPartitions = resources.reduce((s, r) => s + (r.partitions?.length || 0), 0)
    const assignedPartitions = resources.reduce((s, r) => s + (r.partitions?.filter(p => partitionDisplayStatus(p) === 'assigned').length || 0), 0)
    const totalAssignments = resources.reduce((s, r) =>
        s + (r.assignments?.length || 0) + (r.partitions?.filter(p => p.assignment).length || 0)
        , 0)

    /* ------------- Resource CRUD ------------- */

    const handleSaveResource = async (data: any) => {
        try {
            if (editingResource) {
                await api.infrastructure.update(editingResource.id, data)
                setAlert({ type: 'success', message: 'Recurso actualizado.' })
            } else {
                await api.infrastructure.create(data)
                setAlert({ type: 'success', message: 'Recurso creado correctamente.' })
            }
            setShowResourceModal(false)
            setEditingResource(null)
            loadResources()
        } catch {
            setAlert({ type: 'error', message: 'Error al guardar el recurso.' })
        }
    }

    const handleDeleteResource = async (id: number) => {
        if (!confirm('¿Eliminar este recurso y todas sus particiones/asignaciones?')) return
        try {
            await api.infrastructure.delete(id)
            setAlert({ type: 'success', message: 'Recurso eliminado.' })
            loadResources()
        } catch {
            setAlert({ type: 'error', message: 'Error al eliminar el recurso.' })
        }
    }

    /* ------------- Partition CRUD ------------- */

    const handleSavePartition = async (data: any) => {
        try {
            if (editingPartition) {
                await api.infrastructure.updatePartition(editingPartition.id, data)
                setAlert({ type: 'success', message: 'Partición actualizada.' })
            } else {
                await api.infrastructure.createPartition({ ...data, gpu_id: partitionGpuId })
                setAlert({ type: 'success', message: 'Partición creada.' })
            }
            setShowPartitionModal(false)
            setEditingPartition(null)
            setPartitionGpuId(null)
            loadResources()
        } catch {
            setAlert({ type: 'error', message: 'Error al guardar la partición.' })
        }
    }

    const handleDeletePartition = async (id: number) => {
        if (!confirm('¿Eliminar esta partición MiG?')) return
        try {
            await api.infrastructure.deletePartition(id)
            setAlert({ type: 'success', message: 'Partición eliminada.' })
            loadResources()
        } catch {
            setAlert({ type: 'error', message: 'Error al eliminar la partición.' })
        }
    }

    /* ------------- Assignment CRUD ------------- */

    const handleSaveAssignment = async (data: any) => {
        try {
            if (editingAssignment) {
                await api.infrastructure.updateAssignment(editingAssignment.id, data)
                setAlert({ type: 'success', message: 'Asignación actualizada.' })
            } else {
                await api.infrastructure.createAssignment({
                    ...data,
                    resource_id: assignTarget?.resourceId || null,
                    partition_id: assignTarget?.partitionId || null,
                })
                setAlert({ type: 'success', message: 'Asignación creada.' })
            }
            setShowAssignModal(false)
            setAssignTarget(null)
            setEditingAssignment(null)
            loadResources()
        } catch {
            setAlert({ type: 'error', message: 'Error al guardar la asignación.' })
        }
    }

    const handleDeleteAssignment = async (id: number) => {
        if (!confirm('¿Eliminar esta asignación?')) return
        try {
            await api.infrastructure.deleteAssignment(id)
            setAlert({ type: 'success', message: 'Asignación eliminada.' })
            loadResources()
        } catch {
            setAlert({ type: 'error', message: 'Error al eliminar la asignación.' })
        }
    }

    if (loading) return <Page><EmptyState>Cargando infraestructura...</EmptyState></Page>

    if (!canViewInfra || accessDenied) {
        return (
            <Page>
                <PageHeader><PageTitle>🖥️ Infraestructura</PageTitle></PageHeader>
                <EmptyState>No tienes acceso a la vista de Infraestructura. Pide a un administrador que te lo active.</EmptyState>
            </Page>
        )
    }

    return (
        <Page>
            <PageHeader>
                <PageTitle>🖥️ Infraestructura</PageTitle>
                <BtnGroup>
                    {isAdmin && (
                        <Btn onClick={handleRefresh} disabled={refreshing}>
                            {refreshing ? '⏳ Refrescando...' : '🔄 Refrescar'}
                        </Btn>
                    )}
                    {isAdmin && (
                        <Btn $variant="primary" onClick={() => {
                            setEditingResource(null)
                            setShowResourceModal(true)
                        }}>
                            + Nuevo Recurso
                        </Btn>
                    )}
                </BtnGroup>
            </PageHeader>

            {alert && <Alert $type={alert.type}>{alert.message}</Alert>}

            {/* Summary cards */}
            <SummaryCards>
                <SummaryCard>
                    <SummaryValue>{onPremCount}</SummaryValue>
                    <SummaryLabel>GPUs On-Prem</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                    <SummaryValue>{awsCount}</SummaryValue>
                    <SummaryLabel>Recursos AWS</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                    <SummaryValue>{totalPartitions}</SummaryValue>
                    <SummaryLabel>Particiones MiG</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                    <SummaryValue>{assignedPartitions}/{totalPartitions}</SummaryValue>
                    <SummaryLabel>Particiones Asignadas</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                    <SummaryValue>{totalAssignments}</SummaryValue>
                    <SummaryLabel>Asignaciones a Proyectos</SummaryLabel>
                </SummaryCard>
            </SummaryCards>

            {/* Tabs */}
            <TabBar>
                <Tab $active={activeTab === 'on_prem'} onClick={() => setActiveTab('on_prem')}>🖥️ On-Prem ({onPremCount})</Tab>
                <Tab $active={activeTab === 'aws'} onClick={() => setActiveTab('aws')}>☁️ AWS ({awsCount})</Tab>
            </TabBar>

            {/* Resource list */}
            {filtered.length === 0 ? (
                <EmptyState>
                    {activeTab === 'on_prem'
                        ? `No hay GPUs on-prem registradas.${isAdmin ? ' Añade una con el botón "Nuevo Recurso".' : ''}`
                        : 'No hay recursos AWS registrados.'}
                </EmptyState>
            ) : (
                filtered.map(resource => (
                    <Card key={resource.id}>
                        <CardHeader>
                            <div>
                                <CardTitle>
                                    {envLabel(resource.environment)} {resource.name}
                                    <Badge $color={statusColor(resource.status)} style={{ marginLeft: 10 }}>
                                        {statusLabel(resource.status)}
                                    </Badge>
                                </CardTitle>
                            </div>
                            {isAdmin && (
                                <BtnGroup>
                                    {resource.environment === 'on_prem' && (
                                        <Btn onClick={() => {
                                            setPartitionGpuId(resource.id)
                                            setEditingPartition(null)
                                            setShowPartitionModal(true)
                                        }}>+ Partición MiG</Btn>
                                    )}
                                    <Btn onClick={() => {
                                        setEditingResource(resource)
                                        setShowResourceModal(true)
                                    }}>✏️ Editar</Btn>
                                    <Btn $variant="danger" onClick={() => handleDeleteResource(resource.id)}>🗑️</Btn>
                                </BtnGroup>
                            )}
                        </CardHeader>
                        <CardBody>
                            {/* Resource details */}
                            <Table>
                                <tbody>
                                    {resource.resource_type && <tr><td style={{ fontWeight: 600, width: 160 }}>Tipo</td><td>{resource.resource_type}</td></tr>}
                                    {resource.node_name && <tr><td style={{ fontWeight: 600 }}>Nodo</td><td>{resource.node_name}</td></tr>}
                                    {resource.specs && <tr><td style={{ fontWeight: 600 }}>Especificaciones</td><td>{resource.specs}</td></tr>}
                                    {resource.ip_address && <tr><td style={{ fontWeight: 600 }}>IP</td><td><code>{resource.ip_address}</code></td></tr>}
                                    {resource.endpoint && <tr><td style={{ fontWeight: 600 }}>Endpoint</td><td><code>{resource.endpoint}</code></td></tr>}
                                    {resource.notes && <tr><td style={{ fontWeight: 600 }}>Notas</td><td>{resource.notes}</td></tr>}
                                </tbody>
                            </Table>

                            {/* On-prem GPU: MiG Partitions */}
                            {resource.environment === 'on_prem' && (
                                <>
                                    <h4 style={{ marginTop: 20, marginBottom: 8, fontSize: '0.9rem' }}>
                                        Particiones MiG ({resource.partitions?.length || 0})
                                    </h4>
                                    {(!resource.partitions || resource.partitions.length === 0) ? (
                                        <InfoRow>Sin particiones configuradas</InfoRow>
                                    ) : (
                                        <PartitionGrid>
                                            {resource.partitions.map(p => {
                                                const shownStatus = partitionDisplayStatus(p)
                                                return (
                                                <PartitionCard key={p.id} $status={shownStatus}>
                                                    <PartitionHeader>
                                                        <PartitionName>{p.partition_name}</PartitionName>
                                                        <Badge $color={statusColor(shownStatus)}>{statusLabel(shownStatus)}</Badge>
                                                    </PartitionHeader>
                                                    <InfoRow>📦 Perfil: <strong>{p.mig_profile}</strong></InfoRow>
                                                    {p.memory_gb && <InfoRow>💾 Memoria: <strong>{p.memory_gb} GB</strong></InfoRow>}
                                                    {p.assignment ? (
                                                        <>
                                                            <InfoRow>📋 Proyecto: <strong>{p.assignment.opportunity_name || 'Sin proyecto'}</strong></InfoRow>
                                                            {p.assignment.redmine_identifier && (
                                                                <InfoRow>🔗 Redmine: <strong>{p.assignment.redmine_project_name || p.assignment.redmine_identifier}</strong> <span style={{ opacity: 0.7 }}>({p.assignment.redmine_identifier})</span></InfoRow>
                                                            )}
                                                            {p.assignment.start_date && <InfoRow>📅 {p.assignment.start_date} → {p.assignment.end_date || '∞'}</InfoRow>}
                                                            {p.assignment.notes && <InfoRow>📝 {p.assignment.notes}</InfoRow>}
                                                            {isAdmin && (
                                                                <BtnGroup style={{ marginTop: 8 }}>
                                                                    <Btn onClick={() => {
                                                                        setEditingAssignment(p.assignment!)
                                                                        setAssignTarget(null)
                                                                        setShowAssignModal(true)
                                                                    }}>✏️</Btn>
                                                                    <Btn $variant="danger" onClick={() => handleDeleteAssignment(p.assignment!.id)}>🗑️</Btn>
                                                                </BtnGroup>
                                                            )}
                                                        </>
                                                    ) : (
                                                        isAdmin && (
                                                            <Btn style={{ marginTop: 8 }} $variant="primary" onClick={() => {
                                                                setAssignTarget({ partitionId: p.id })
                                                                setEditingAssignment(null)
                                                                setShowAssignModal(true)
                                                            }}>Asignar a proyecto</Btn>
                                                        )
                                                    )}
                                                    {isAdmin && (
                                                        <BtnGroup style={{ marginTop: 6 }}>
                                                            <Btn onClick={() => {
                                                                setEditingPartition(p)
                                                                setPartitionGpuId(p.gpu_id)
                                                                setShowPartitionModal(true)
                                                            }}>✏️ Editar</Btn>
                                                            <Btn $variant="danger" onClick={() => handleDeletePartition(p.id)}>🗑️</Btn>
                                                        </BtnGroup>
                                                    )}
                                                </PartitionCard>
                                                )
                                            })}
                                        </PartitionGrid>
                                    )}
                                </>
                            )}

                            {/* AWS resources: Assignments */}
                            {resource.environment === 'aws' && (
                                <>
                                    <h4 style={{ marginTop: 20, marginBottom: 8, fontSize: '0.9rem' }}>
                                        Asignaciones ({resource.assignments?.length || 0})
                                        {isAdmin && (
                                            <Btn style={{ marginLeft: 12 }} $variant="primary" onClick={() => {
                                                setAssignTarget({ resourceId: resource.id })
                                                setEditingAssignment(null)
                                                setShowAssignModal(true)
                                            }}>+ Asignar</Btn>
                                        )}
                                    </h4>
                                    {(!resource.assignments || resource.assignments.length === 0) ? (
                                        <InfoRow>Sin asignaciones</InfoRow>
                                    ) : (
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>Proyecto</th>
                                                    <th>Redmine</th>
                                                    <th>Fecha Inicio</th>
                                                    <th>Fecha Fin</th>
                                                    <th>Notas</th>
                                                    {isAdmin && <th></th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {resource.assignments.map(a => (
                                                    <tr key={a.id}>
                                                        <td>{a.opportunity_name || 'Sin proyecto'}</td>
                                                        <td title={a.redmine_project_name || undefined}>{a.redmine_identifier ? `🔗 ${a.redmine_identifier}` : '—'}</td>
                                                        <td>{a.start_date || '—'}</td>
                                                        <td>{a.end_date || '—'}</td>
                                                        <td>{a.notes || '—'}</td>
                                                        {isAdmin && (
                                                            <td>
                                                                <BtnGroup>
                                                                    <Btn onClick={() => {
                                                                        setEditingAssignment(a)
                                                                        setAssignTarget(null)
                                                                        setShowAssignModal(true)
                                                                    }}>✏️</Btn>
                                                                    <Btn $variant="danger" onClick={() => handleDeleteAssignment(a.id)}>🗑️</Btn>
                                                                </BtnGroup>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </>
                            )}
                        </CardBody>
                    </Card>
                ))
            )}

            {/* ===== Modals ===== */}

            {showResourceModal && (
                <ResourceModal
                    resource={editingResource}
                    defaultEnv={activeTab}
                    onSave={handleSaveResource}
                    onClose={() => { setShowResourceModal(false); setEditingResource(null) }}
                />
            )}

            {showPartitionModal && (
                <PartitionModal
                    partition={editingPartition}
                    onSave={handleSavePartition}
                    onClose={() => { setShowPartitionModal(false); setEditingPartition(null); setPartitionGpuId(null) }}
                />
            )}

            {showAssignModal && (
                <AssignmentModal
                    assignment={editingAssignment}
                    opportunities={opportunities}
                    onSave={handleSaveAssignment}
                    onClose={() => { setShowAssignModal(false); setAssignTarget(null); setEditingAssignment(null) }}
                />
            )}
        </Page>
    )
}

/* ================================================================
   Resource Form Modal
   ================================================================ */

const ResourceModal: React.FC<{
    resource: Resource | null
    defaultEnv: 'on_prem' | 'aws'
    onSave: (data: any) => void
    onClose: () => void
}> = ({ resource, defaultEnv, onSave, onClose }) => {
    const [form, setForm] = useState({
        name: resource?.name || '',
        environment: resource?.environment || defaultEnv,
        resource_type: resource?.resource_type || (defaultEnv === 'on_prem' ? 'gpu' : 'ec2'),
        node_name: resource?.node_name || '',
        specs: resource?.specs || '',
        ip_address: resource?.ip_address || '',
        endpoint: resource?.endpoint || '',
        status: resource?.status || 'active',
        notes: resource?.notes || '',
    })

    const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }))

    return (
        <FormOverlay onClick={onClose}>
            <FormModal onClick={e => e.stopPropagation()}>
                <FormTitle>{resource ? 'Editar Recurso' : 'Nuevo Recurso'}</FormTitle>
                <FormGroup>
                    <FormLabel>Nombre *</FormLabel>
                    <FormInput value={form.name} onChange={e => set('name', e.target.value)} placeholder="GPU-Node-01" />
                </FormGroup>
                <FormRow>
                    <FormGroup>
                        <FormLabel>Entorno</FormLabel>
                        <FormSelect value={form.environment} onChange={e => set('environment', e.target.value)}>
                            <option value="on_prem">🖥️ On-Prem</option>
                            <option value="aws">☁️ AWS</option>
                        </FormSelect>
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Tipo</FormLabel>
                        <FormInput value={form.resource_type} onChange={e => set('resource_type', e.target.value)}
                            placeholder={form.environment === 'on_prem' ? 'gpu' : 'ec2, sagemaker, eks...'} />
                    </FormGroup>
                </FormRow>
                <FormRow>
                    <FormGroup>
                        <FormLabel>Nodo / Región</FormLabel>
                        <FormInput value={form.node_name} onChange={e => set('node_name', e.target.value)}
                            placeholder={form.environment === 'on_prem' ? 'k8s-gpu-node-01' : 'eu-west-1'} />
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Especificaciones</FormLabel>
                        <FormInput value={form.specs} onChange={e => set('specs', e.target.value)}
                            placeholder={form.environment === 'on_prem' ? 'NVIDIA A100 80GB' : 'p3.2xlarge'} />
                    </FormGroup>
                </FormRow>
                <FormRow>
                    <FormGroup>
                        <FormLabel>IP</FormLabel>
                        <FormInput value={form.ip_address} onChange={e => set('ip_address', e.target.value)} placeholder="10.0.1.50" />
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Endpoint</FormLabel>
                        <FormInput value={form.endpoint} onChange={e => set('endpoint', e.target.value)} placeholder="https://..." />
                    </FormGroup>
                </FormRow>
                <FormGroup>
                    <FormLabel>Estado</FormLabel>
                    <FormSelect value={form.status} onChange={e => set('status', e.target.value)}>
                        <option value="active">Activo</option>
                        <option value="maintenance">Mantenimiento</option>
                        <option value="retired">Retirado</option>
                    </FormSelect>
                </FormGroup>
                <FormGroup>
                    <FormLabel>Notas</FormLabel>
                    <FormInput value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Notas..." />
                </FormGroup>
                <BtnGroup style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                    <Btn onClick={onClose}>Cancelar</Btn>
                    <Btn $variant="primary" disabled={!form.name.trim()} onClick={() => onSave(form)}>
                        {resource ? 'Guardar' : 'Crear'}
                    </Btn>
                </BtnGroup>
            </FormModal>
        </FormOverlay>
    )
}

/* ================================================================
   Partition Form Modal
   ================================================================ */

const PartitionModal: React.FC<{
    partition: Partition | null
    onSave: (data: any) => void
    onClose: () => void
}> = ({ partition, onSave, onClose }) => {
    const [form, setForm] = useState({
        partition_name: partition?.partition_name || '',
        mig_profile: partition?.mig_profile || '1g.5gb',
        memory_gb: partition?.memory_gb?.toString() || '',
        status: partition?.status || 'available',
    })

    const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }))

    return (
        <FormOverlay onClick={onClose}>
            <FormModal onClick={e => e.stopPropagation()}>
                <FormTitle>{partition ? 'Editar Partición MiG' : 'Nueva Partición MiG'}</FormTitle>
                <FormGroup>
                    <FormLabel>Nombre de Partición *</FormLabel>
                    <FormInput value={form.partition_name} onChange={e => set('partition_name', e.target.value)} placeholder="mig-0" />
                </FormGroup>
                <FormRow>
                    <FormGroup>
                        <FormLabel>Perfil MiG *</FormLabel>
                        <FormSelect value={form.mig_profile} onChange={e => set('mig_profile', e.target.value)}>
                            <option value="1g.5gb">1g.5gb</option>
                            <option value="1g.10gb">1g.10gb</option>
                            <option value="2g.10gb">2g.10gb</option>
                            <option value="2g.20gb">2g.20gb</option>
                            <option value="3g.20gb">3g.20gb</option>
                            <option value="3g.40gb">3g.40gb</option>
                            <option value="4g.20gb">4g.20gb</option>
                            <option value="4g.40gb">4g.40gb</option>
                            <option value="7g.40gb">7g.40gb</option>
                            <option value="7g.80gb">7g.80gb</option>
                        </FormSelect>
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Memoria (GB)</FormLabel>
                        <FormInput type="number" value={form.memory_gb} onChange={e => set('memory_gb', e.target.value)} placeholder="5" />
                    </FormGroup>
                </FormRow>
                <FormGroup>
                    <FormLabel>Estado</FormLabel>
                    <FormSelect value={form.status} onChange={e => set('status', e.target.value)}>
                        <option value="available">Disponible</option>
                        <option value="assigned">Asignada</option>
                        <option value="maintenance">Mantenimiento</option>
                    </FormSelect>
                </FormGroup>
                <BtnGroup style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                    <Btn onClick={onClose}>Cancelar</Btn>
                    <Btn $variant="primary" disabled={!form.partition_name.trim()} onClick={() => onSave({
                        ...form,
                        memory_gb: form.memory_gb ? parseFloat(form.memory_gb) : null,
                    })}>
                        {partition ? 'Guardar' : 'Crear'}
                    </Btn>
                </BtnGroup>
            </FormModal>
        </FormOverlay>
    )
}

/* ================================================================
   Assignment Form Modal
   ================================================================ */

const AssignmentModal: React.FC<{
    assignment: Assignment | null
    opportunities: Opportunity[]
    onSave: (data: any) => void
    onClose: () => void
}> = ({ assignment, opportunities, onSave, onClose }) => {
    const [form, setForm] = useState({
        opportunity_id: assignment?.opportunity_id?.toString() || '',
        start_date: assignment?.start_date || '',
        end_date: assignment?.end_date || '',
        notes: assignment?.notes || '',
    })

    const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }))

    return (
        <FormOverlay onClick={onClose}>
            <FormModal onClick={e => e.stopPropagation()}>
                <FormTitle>{assignment ? 'Editar Asignación' : 'Asignar a Proyecto'}</FormTitle>
                <FormGroup>
                    <FormLabel>Proyecto</FormLabel>
                    <FormSelect value={form.opportunity_id} onChange={e => set('opportunity_id', e.target.value)}>
                        <option value="">— Sin proyecto —</option>
                        {opportunities.map(o => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                    </FormSelect>
                </FormGroup>
                <FormRow>
                    <FormGroup>
                        <FormLabel>Fecha Inicio</FormLabel>
                        <FormInput type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Fecha Fin</FormLabel>
                        <FormInput type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
                    </FormGroup>
                </FormRow>
                <FormGroup>
                    <FormLabel>Notas</FormLabel>
                    <FormInput value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Datos adicionales..." />
                </FormGroup>
                <BtnGroup style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                    <Btn onClick={onClose}>Cancelar</Btn>
                    <Btn $variant="primary" onClick={() => onSave({
                        opportunity_id: form.opportunity_id ? parseInt(form.opportunity_id) : null,
                        start_date: form.start_date || null,
                        end_date: form.end_date || null,
                        notes: form.notes || null,
                    })}>
                        {assignment ? 'Guardar' : 'Asignar'}
                    </Btn>
                </BtnGroup>
            </FormModal>
        </FormOverlay>
    )
}

export default InfrastructurePage
